import {
  addProps,
  addDisplayName,
  addState,
  addWrapper,
  flowMax,
  SimpleUnchangedProps,
  addEffect,
  addHandlers,
} from 'ad-hok'
import { getContextHelpers, toObjectKeys } from 'ad-hok-utils'
import { FC } from 'react'
import { typedAs } from 'shared/src/utils/typedAs'
import { createUseStyles } from 'react-jss'
import cx from 'classnames'

import { LABELS_STYLE, NOTES_MEDIUM_STYLE } from './styles'
import { TOAST_Z_INDEX } from '../utils/zIndex'
import { IconButton } from './IconButton'
import { SIDE_PADDING } from './PageConstants'
import { Layout } from './Layout'

const useStyles = createUseStyles({
  layoutContainer: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    zIndex: TOAST_Z_INDEX,
    paddingTop: 0,
    paddingBottom: 0,
    pointerEvents: 'none',
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: 'rgba(54, 52, 50, 0.96)',
    boxShadow: '4px 8px 16px -8px rgba(0, 0, 0, 0.16)',
    borderRadius: 4,
    paddingLeft: 16,
    paddingTop: 8,
    paddingRight: 16,
    paddingBottom: 8,
    marginLeft: SIDE_PADDING,
    marginRight: SIDE_PADDING,
    marginTop: 41,
    position: 'relative',
    pointerEvents: 'auto',
  },
  containerError: {
    backgroundColor: '#F57561',
  },
  title: {
    ...NOTES_MEDIUM_STYLE,
    color: 'white',
  },
  details: {
    ...LABELS_STYLE,
    color: 'rgba(255, 255, 255, 0.56)',
  },
  closeButton: {
    position: 'absolute',
    top: -6,
    right: -6,
  },
  closeIcon: {
    color: 'white',
    width: 16,
    height: 16,
  },
})

type MessageType = 'success' | 'error'

interface MessageWithoutType {
  title: string
  details: string
}

interface Message extends MessageWithoutType {
  type: MessageType
}

interface Props {
  message: Message
  onDismiss: () => void
}

const Toast: FC<Props> = flowMax(
  addDisplayName('Toast'),
  addProps(() => ({
    classes: useStyles(),
  })),
  ({ message: { type, title, details }, onDismiss, classes }) => (
    <Layout mode="creator" className={classes.layoutContainer}>
      <div
        className={cx(
          classes.container,
          type === 'error' && classes.containerError
        )}
      >
        <p className={classes.title}>{title}</p>
        <p className={classes.details}>{details}</p>
        <IconButton
          onClick={onDismiss}
          name="close"
          className={classes.closeButton}
          iconClassName={classes.closeIcon}
          addHitSlop
        />
      </div>
    </Layout>
  )
)

interface ToastContext {
  showErrorToast: (message: MessageWithoutType) => void
  showSuccessToast: (message: MessageWithoutType) => void
}

const [addToastContextProviderBase, addToastContext] =
  getContextHelpers<ToastContext>(
    toObjectKeys(['showErrorToast', 'showSuccessToast'])
  )

export { addToastContext }

const SHOW_DURATION_MS = 5000

export const addToastContextProvider: SimpleUnchangedProps = flowMax(
  addState('_message', 'setMessage', typedAs<Message | null>(null)),
  addEffect(
    ({ _message, setMessage }) =>
      () => {
        if (!(_message?.type === 'success')) return

        const timer = setTimeout(() => {
          setMessage(null)
        }, SHOW_DURATION_MS)

        return () => {
          clearTimeout(timer)
        }
      },
    ['_message']
  ),
  addHandlers({
    showErrorToast:
      ({ setMessage }) =>
      (message: MessageWithoutType) => {
        setMessage({
          ...message,
          type: 'error',
        })
      },
    showSuccessToast:
      ({ setMessage }) =>
      (message: MessageWithoutType) => {
        setMessage({
          ...message,
          type: 'success',
        })
      },
  }),
  addToastContextProviderBase,
  addWrapper((render, { _message, setMessage }) => (
    <>
      {_message && (
        <Toast message={_message} onDismiss={() => setMessage(null)} />
      )}
      {render()}
    </>
  ))
)
